import React from 'react';
import styled from '@emotion/styled/macro';
import { LoadingMeter, Notice } from '../base';
import { Badge } from '../badge/badge';
import { BadgeType } from '../badge/badge.styles';
import { PENDING_PRICES_MESSAGE } from '../claim-details/hl7-snapshot/pending-prices-message';
import * as SectionS from '../claim-details/claim-section.styles';
import { ClaimFinancialSummary } from './claim-financial-summary';
import { ClaimFinancialSummaryData } from './claim-financial-summary.utils';

const TitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const UpdatingNotice = styled(Notice)`
    margin: 0 24px 12px;
`;

const Body = styled.div`
    margin: 0 24px;
`;

interface IClaimFinancialsSectionProps {
    /** True while any input to the totals (template, payments, collections, chargemaster) is still loading. */
    loading: boolean;
    financials?: ClaimFinancialSummaryData;
    /** Newer claim data is still processing: the totals come from the last processed version. */
    updating?: boolean;
}

/**
 * "Claim Financials" block for the embedded claim panel, between Claim Overview and the services
 * table. While inputs load it shows a loader rather than zeros: a $0.00 tile would assert a result
 * we have not computed (NS-3).
 */
export const ClaimFinancialsSection: React.FC<IClaimFinancialsSectionProps> = ({ loading, financials, updating }) => (
    <SectionS.ClaimSectionBlock>
        <SectionS.ClaimSectionHeader>
            <TitleGroup>
                <SectionS.ClaimSectionTitle>Claim Financials</SectionS.ClaimSectionTitle>
                {updating && (
                    <Badge type={BadgeType.BLUE_LIGHT} size="small" title={PENDING_PRICES_MESSAGE}>
                        Updating
                    </Badge>
                )}
            </TitleGroup>
        </SectionS.ClaimSectionHeader>
        {updating && <UpdatingNotice tone="attention">{PENDING_PRICES_MESSAGE}</UpdatingNotice>}
        <Body>{loading || !financials ? <LoadingMeter /> : <ClaimFinancialSummary financials={financials} />}</Body>
    </SectionS.ClaimSectionBlock>
);
